import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, useDraggable, useDroppable, PointerSensor,
  TouchSensor, MouseSensor, useSensor, useSensors, DragOverlay
} from '@dnd-kit/core';
import { snapCenterToCursor, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft, ChevronRight, Search, Check, X, Users,
  Calendar as CalIcon, Clock, RotateCcw, ArrowDownToLine
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POOL_DROP_ID = '__pool__';

const POOL_ZONE_BG = 'bg-slate-50';
const DROP_ZONE_BG = 'bg-blue-50';
const DROP_ZONE_BORDER = 'border-blue-300';

const empKey = (e) => e.user_id || e.id;
const empName = (e) => e.user?.name || e.name || e.full_name || e.user?.email || e.email || 'Employee';
const empInitials = (e) => empName(e).split(' ').map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase();
// Display name for tight grid cells — keeps first + last when there are 3+
// words, so "John Q Smith" reads as "John Smith" instead of being truncated
// to "John Q…". For 1-2 word names we return the full name as-is.
const empDisplayName = (e) => {
  const full = empName(e);
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 2) return full;
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const nextWorkingDay = () => {
  const d = new Date();
  d.setDate(d.getDate()+1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate()+1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// ------------------------------- Chip ---------------------------------------
const Chip = ({ id, employee, disabled, onRemove, compact, hint, gridCell }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled, data: { employee } });
  // In `gridCell` mode each chip fills its grid track at equal width and the
  // name truncates with an ellipsis. Outside grid mode (e.g. inside a drop
  // zone) the chip keeps its old inline shape so it hugs the name.
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`mob-sched-chip-${empKey(employee)}`}
      className={[
        'select-none touch-none rounded-full border',
        gridCell ? 'flex w-full items-center gap-1 px-1.5 py-1 text-[11px]' :
          (compact ? 'inline-flex items-center gap-1.5 pl-2 pr-1 py-1 text-xs' :
            'inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 text-sm'),
        disabled ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white text-slate-800 border-slate-300 shadow-sm cursor-grab active:cursor-grabbing hover:border-blue-400',
        isDragging ? 'opacity-30' : ''
      ].join(' ')}
      style={{ minHeight: gridCell ? 32 : 36 }}
    >
      <span className={[
        'inline-flex items-center justify-center rounded-full font-semibold shrink-0',
        gridCell ? 'w-5 h-5 text-[9px]' :
          (compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-[11px]'),
        disabled ? 'bg-slate-200 text-slate-500' : 'bg-blue-100 text-blue-700'
      ].join(' ')}>{empInitials(employee)}</span>
      <span className={gridCell ? 'font-medium truncate min-w-0 flex-1 text-left' : 'font-medium whitespace-nowrap'}>
        {gridCell ? empDisplayName(employee) : empName(employee)}
      </span>
      {hint && !gridCell && <span className="text-[10px] text-amber-600 ml-0.5">{hint}</span>}
      {onRemove && !disabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 rounded-full hover:bg-red-100 p-0.5 text-red-500 shrink-0"
          data-testid={`mob-sched-chip-remove-${empKey(employee)}`}
          aria-label={`Remove ${empName(employee)}`}
        >
          <X className={gridCell ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        </button>
      )}
    </div>
  );
};

// --------------------------- Droppable wrapper ------------------------------
const DropArea = ({ id, children, className, isEmpty, label, testId }) => {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      data-testid={testId}
      className={[
        'rounded-xl border-2 border-dashed transition-colors',
        isOver ? 'border-blue-500 bg-blue-100' : `${DROP_ZONE_BORDER} ${DROP_ZONE_BG}`,
        'min-h-[110px] p-3',
        className || ''
      ].join(' ')}
    >
      {isEmpty ? (
        <div className="h-full flex flex-col items-center justify-center text-blue-400 text-xs py-6 pointer-events-none">
          <ArrowDownToLine className="w-5 h-5 mb-1.5" />
          <span>{label || 'Drag employees here'}</span>
        </div>
      ) : children}
    </div>
  );
};

// ---------------------------- Main Builder ----------------------------------
export function MobileScheduleBuilder({ open, onOpenChange, projects, employees, onSaved }) {
  const [date, setDate] = useState(nextWorkingDay());
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('recent'); // 'recent' | 'alpha'
  const [poolMode, setPoolMode] = useState('field');  // 'field' | 'office'
  // assignmentsByProject: { [projectId]: { team_members: [user_id], start_time, end_time, saved, savedAt } }
  const [byProject, setByProject] = useState({});
  const [scheduleSnapshot, setScheduleSnapshot] = useState([]); // schedules from [-14d, +1d] window
  const [loadingDate, setLoadingDate] = useState(false);
  const [savingProj, setSavingProj] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeDragId, setActiveDragId] = useState(null);
  const carouselRef = useRef(null);

  const activeProjects = useMemo(
    () => (projects || []).filter(p => (p.status || 'active') === 'active' || p.status === 'in_progress'),
    [projects]
  );

  // -------- sensors: drag starts after small movement (no accidental drags) --
  // The TouchSensor delay was a long 120ms originally so accidental taps
  // wouldn't kick off a drag. Combined with `snapCenterToCursor` we now use a
  // shorter delay because the chip is anchored directly under the finger
  // instead of floating offset by the original chip position.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 90, tolerance: 8 } }),
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // -------- fetch schedule window when date or open changes -----------------
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const d = new Date(date + 'T00:00:00');
    const from = new Date(d); from.setDate(from.getDate() - 14);
    const to = new Date(d); to.setDate(to.getDate() + 1);
    const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
    setLoadingDate(true);
    fetch(`${API_URL}/api/schedules?start_date=${fmt(from)}&end_date=${fmt(to)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then((data) => setScheduleSnapshot(Array.isArray(data) ? data : []))
      .catch(() => setScheduleSnapshot([]))
      .finally(() => setLoadingDate(false));
  }, [open, date]);

  // -------- reset when dialog opens -----------------------------------------
  useEffect(() => {
    if (open) {
      setByProject({});
      setActiveIdx(0);
      setSearch('');
    }
  }, [open]);

  // -------- derived: who's booked elsewhere on this date (DB) ---------------
  const bookedElsewhereMap = useMemo(() => {
    // { user_id: project_name } for schedules on `date`
    const map = {};
    for (const s of scheduleSnapshot) {
      if ((s.date || '').split('T')[0] !== date) continue;
      for (const uid of (s.team_members || [])) {
        if (!map[uid]) map[uid] = s.project_name || 'another project';
      }
    }
    return map;
  }, [scheduleSnapshot, date]);

  // -------- derived: last-day team per project ------------------------------
  const lastDayTeamByProject = useMemo(() => {
    // for each project, find most recent date < `date` and return its team
    const buckets = {}; // proj -> [{date, team_members}]
    for (const s of scheduleSnapshot) {
      const sd = (s.date || '').split('T')[0];
      if (!sd || sd >= date) continue;
      if (!s.project_id) continue;
      buckets[s.project_id] = buckets[s.project_id] || [];
      buckets[s.project_id].push({ date: sd, members: s.team_members || [] });
    }
    const out = {};
    Object.entries(buckets).forEach(([pid, arr]) => {
      arr.sort((a, b) => b.date.localeCompare(a.date));
      const top = arr[0];
      if (top) out[pid] = { date: top.date, members: top.members };
    });
    return out;
  }, [scheduleSnapshot, date]);

  // -------- derived: project recency (last scheduled date) ------------------
  const projectRecency = useMemo(() => {
    const map = {};
    for (const s of scheduleSnapshot) {
      const sd = (s.date || '').split('T')[0];
      if (!s.project_id || !sd) continue;
      if (!map[s.project_id] || sd > map[s.project_id]) map[s.project_id] = sd;
    }
    return map;
  }, [scheduleSnapshot]);

  // -------- sorted projects -------------------------------------------------
  const sortedProjects = useMemo(() => {
    const arr = [...activeProjects];
    if (sortMode === 'alpha') {
      arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      arr.sort((a, b) => {
        const ra = projectRecency[a.id] || '';
        const rb = projectRecency[b.id] || '';
        if (ra !== rb) return rb.localeCompare(ra); // recent first
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return arr;
  }, [activeProjects, sortMode, projectRecency]);

  // -------- derived: assigned-in-this-session set ---------------------------
  const sessionAssignedSet = useMemo(() => {
    const s = new Set();
    Object.values(byProject).forEach(p => (p?.team_members || []).forEach(uid => s.add(uid)));
    return s;
  }, [byProject]);

  // Field-employee roles — i.e. people who actually go to job sites. The
  // schedule builder pool defaults to these. Office staff (admin/GM/accounts
  // etc.) only appear when the user toggles the pool selector to 'office'.
  const FIELD_ROLES = new Set(['employee', 'subcontractor', 'team_lead', 'project_coordinator']);
  const isFieldEmployee = (emp) => {
    // Prefer the explicit employee_type marker when present; otherwise fall
    // back to role lookup so older accounts without `employee_type` still work.
    const t = emp.employee_type || emp.user?.employee_type;
    if (t === 'field_based') return true;
    if (t === 'office_based') return false;
    const role = emp.user?.role || emp.role;
    return role ? FIELD_ROLES.has(role) : true;
  };
  // Paused/blocked accounts have `is_active === false` on either the
  // employee record or the underlying user record. Default to true so a
  // missing flag doesn't hide everyone.
  const isAccountActive = (emp) => {
    if (emp.is_active === false) return false;
    if (emp.user && emp.user.is_active === false) return false;
    if (emp.status === 'paused' || emp.status === 'blocked' || emp.status === 'suspended') return false;
    return true;
  };

  // -------- the pool list (filtered + deduped by user_id/id) ----------------
  const visiblePool = useMemo(() => {
    const q = search.trim().toLowerCase();
    const seen = new Set();
    const out = [];
    for (const emp of (employees || [])) {
      const uid = empKey(emp);
      if (seen.has(uid)) continue; // de-dupe by user_id/id to avoid duplicate React keys
      seen.add(uid);
      if (!isAccountActive(emp)) continue;          // skip paused/blocked accounts
      const field = isFieldEmployee(emp);
      if (poolMode === 'field' && !field) continue; // 'Field staff' tab
      if (poolMode === 'office' && field) continue; // 'Office team' tab
      if (sessionAssignedSet.has(uid)) continue;    // moved into a drop zone in this session
      if (q && !empName(emp).toLowerCase().includes(q)) continue;
      out.push(emp);
    }
    return out;
  }, [employees, search, sessionAssignedSet, poolMode]);

  // Counts for the segmented toggle so the user can see how many people are
  // available in each bucket without switching tabs.
  const poolCounts = useMemo(() => {
    let field = 0, office = 0;
    const seen = new Set();
    for (const emp of (employees || [])) {
      const uid = empKey(emp);
      if (seen.has(uid)) continue;
      seen.add(uid);
      if (!isAccountActive(emp)) continue;
      if (isFieldEmployee(emp)) field += 1; else office += 1;
    }
    return { field, office };
  }, [employees]);

  // -------- helpers ---------------------------------------------------------
  const updateProj = (pid, patch) => {
    setByProject(prev => ({
      ...prev,
      [pid]: { team_members: [], start_time: '07:00', end_time: '15:00', saved: false, ...(prev[pid] || {}), ...patch }
    }));
  };
  const addToProject = (pid, uid) => {
    setByProject(prev => {
      const cur = prev[pid] || { team_members: [], start_time: '07:00', end_time: '15:00', saved: false };
      if (cur.team_members.includes(uid)) return prev;
      return { ...prev, [pid]: { ...cur, team_members: [...cur.team_members, uid], saved: false } };
    });
  };
  const removeFromProject = (pid, uid) => {
    setByProject(prev => {
      const cur = prev[pid];
      if (!cur) return prev;
      return { ...prev, [pid]: { ...cur, team_members: cur.team_members.filter(x => x !== uid), saved: false } };
    });
  };

  // -------- onDragEnd -------------------------------------------------------
  const handleDragEnd = (ev) => {
    setActiveDragId(null);
    const { active, over } = ev;
    if (!over) return;
    const fromId = String(active.id);
    const toId = String(over.id);
    if (fromId === toId) return;
    // active.id format: `pool:${uid}` or `proj:${pid}:${uid}`
    // over.id format: POOL_DROP_ID or `proj:${pid}`
    if (fromId.startsWith('pool:')) {
      const uid = fromId.split(':')[1];
      if (toId.startsWith('proj:')) {
        const pid = toId.slice(5);
        if (bookedElsewhereMap[uid]) {
          toast.warning(`${empName(active.data?.current?.employee || { name: 'Employee' })} is already on ${bookedElsewhereMap[uid]} on this date.`);
          return;
        }
        addToProject(pid, uid);
      }
      return;
    }
    if (fromId.startsWith('proj:')) {
      const [, pid, uid] = fromId.split(':');
      if (toId === POOL_DROP_ID) {
        removeFromProject(pid, uid);
      } else if (toId.startsWith('proj:')) {
        const toPid = toId.slice(5);
        if (toPid === pid) return;
        removeFromProject(pid, uid);
        addToProject(toPid, uid);
      }
    }
  };

  // -------- save one project's schedule -------------------------------------
  const saveProject = async (pid) => {
    const proj = byProject[pid];
    if (!proj || proj.team_members.length === 0) {
      toast.error('Drag at least one employee in first.');
      return false;
    }
    const token = localStorage.getItem('token');
    setSavingProj(pid);
    try {
      const res = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: pid,
          date,
          team_members: proj.team_members,
          is_recurring: false,
          start_time: proj.start_time || '07:00',
          end_time: proj.end_time || '15:00',
          notes: '',
          status: 'scheduled'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Could not save');
        return false;
      }
      if (data.status === 'conflict') {
        toast.warning('Some employees clash with existing schedules. Open the full Create Schedule dialog to resolve.');
        return false;
      }
      updateProj(pid, { saved: true, savedAt: new Date().toISOString() });
      toast.success(data.message || 'Schedule saved');
      if (onSaved) onSaved();
      return true;
    } catch {
      toast.error('Connection error');
      return false;
    } finally {
      setSavingProj(null);
    }
  };

  const saveAndNext = async (pid) => {
    const ok = await saveProject(pid);
    if (ok) goNext();
  };

  // -------- carousel nav ----------------------------------------------------
  const scrollToCard = (idx) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector(`[data-card-idx="${idx}"]`);
    if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };
  const goPrev = () => { const n = Math.max(0, activeIdx - 1); setActiveIdx(n); scrollToCard(n); };
  const goNext = () => { const n = Math.min(sortedProjects.length - 1, activeIdx + 1); setActiveIdx(n); scrollToCard(n); };

  // Track scroll position → activeIdx
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let timer = null;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const cards = el.querySelectorAll('[data-card-idx]');
        const elRect = el.getBoundingClientRect();
        let best = 0, bestDist = Infinity;
        cards.forEach(c => {
          const r = c.getBoundingClientRect();
          const dist = Math.abs(r.left - elRect.left);
          if (dist < bestDist) { bestDist = dist; best = parseInt(c.dataset.cardIdx); }
        });
        setActiveIdx(best);
      }, 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [sortedProjects.length]);

  // -------- render ----------------------------------------------------------
  const activeDragEmployee = useMemo(() => {
    if (!activeDragId) return null;
    if (activeDragId.startsWith('pool:')) {
      const uid = activeDragId.split(':')[1];
      return (employees || []).find(e => empKey(e) === uid);
    }
    if (activeDragId.startsWith('proj:')) {
      const uid = activeDragId.split(':')[2];
      return (employees || []).find(e => empKey(e) === uid);
    }
    return null;
  }, [activeDragId, employees]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[95vh] overflow-y-auto p-4 sm:p-6" data-testid="mob-sched-builder-dialog" aria-describedby="mob-sched-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-blue-600" />
            Quick Schedule Builder
          </DialogTitle>
          <DialogDescription id="mob-sched-desc" className="text-xs">
            Drag employees from the pool into a project, then <b>Save & Next</b> to slide to the next project.
          </DialogDescription>
        </DialogHeader>

        <DndContext
          sensors={sensors}
          modifiers={[snapCenterToCursor, restrictToWindowEdges]}
          onDragStart={(ev) => setActiveDragId(String(ev.active.id))}
          onDragCancel={() => setActiveDragId(null)}
          onDragEnd={handleDragEnd}
        >
          {/* ------ Date + sort toggle ------ */}
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="mob-sched-date" className="text-xs">Date</Label>
              <div className="relative">
                <CalIcon className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  id="mob-sched-date"
                  type="date"
                  value={date}
                  min={todayISO()}
                  onChange={(e) => setDate(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="mob-sched-date-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <span className="text-slate-500 mr-1">Order:</span>
              <Button
                type="button" size="sm"
                variant={sortMode === 'recent' ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => setSortMode('recent')}
                data-testid="mob-sched-sort-recent"
              >Recent</Button>
              <Button
                type="button" size="sm"
                variant={sortMode === 'alpha' ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => setSortMode('alpha')}
                data-testid="mob-sched-sort-alpha"
              >A–Z</Button>
            </div>
          </div>

          {/* ------ Pool (top) ------ */}
          <div className={`rounded-xl border ${POOL_ZONE_BG} mb-4`}>
            <PoolDrop id={POOL_DROP_ID}>
              <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap bg-white/60 rounded-t-xl">
                <span className="text-xs font-semibold text-slate-700">Unassigned</span>
                <Badge variant="secondary" className="text-[10px]" data-testid="mob-sched-pool-count">{visiblePool.length}</Badge>
                {/* Field / Office segmented toggle — defaults to Field, which
                    is the common case. Office is opt-in for the rare moment a
                    project manager needs to draft an office person onto a
                    crew. Counts come from `poolCounts` so the user can see
                    how many people sit in each bucket without flipping. */}
                <div className="ml-auto inline-flex items-center rounded-full border border-slate-300 bg-white p-0.5 text-[10px]" data-testid="mob-sched-pool-mode">
                  <button
                    type="button"
                    onClick={() => setPoolMode('field')}
                    className={[
                      'px-2 py-0.5 rounded-full font-semibold transition-colors',
                      poolMode === 'field' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:text-slate-900'
                    ].join(' ')}
                    data-testid="mob-sched-pool-mode-field"
                  >
                    Field <span className="opacity-70">({poolCounts.field})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPoolMode('office')}
                    className={[
                      'px-2 py-0.5 rounded-full font-semibold transition-colors',
                      poolMode === 'office' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:text-slate-900'
                    ].join(' ')}
                    data-testid="mob-sched-pool-mode-office"
                  >
                    Office <span className="opacity-70">({poolCounts.office})</span>
                  </button>
                </div>
              </div>
              {/* Capped chip pool with a visibly THICK scrollbar so it can be
                  grabbed easily on touch. `pool-thick-scroll` class is styled
                  inline below. */}
              <div
                className="p-2 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[260px] overflow-y-auto overscroll-contain pool-thick-scroll"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {visiblePool.length === 0 ? (
                  <div className="col-span-3 sm:col-span-4 text-center text-xs text-slate-400 py-4">
                    {loadingDate
                      ? 'Loading…'
                      : poolMode === 'office'
                        ? 'No office staff available. Toggle to Field for crew members.'
                        : 'No unassigned field employees. All matched employees are on a project for this date.'}
                  </div>
                ) : visiblePool.map(emp => (
                  <Chip
                    key={empKey(emp)}
                    id={`pool:${empKey(emp)}`}
                    employee={emp}
                    disabled={!!bookedElsewhereMap[empKey(emp)]}
                    hint={bookedElsewhereMap[empKey(emp)] ? `@ ${bookedElsewhereMap[empKey(emp)]}` : null}
                    gridCell
                  />
                ))}
              </div>
              {/* Search bar BELOW the pool — easier thumb reach on phones. */}
              <div className="px-2 pb-2 pt-1 border-t bg-white/40 rounded-b-xl">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search employee…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-7 h-8 text-xs"
                    data-testid="mob-sched-search"
                  />
                </div>
              </div>
            </PoolDrop>
          </div>
          {/* Thick visible scrollbar styling for the chip pool — easier to grab
              with a thumb on touch screens, default WebKit one is too thin. */}
          <style>{`
            .pool-thick-scroll::-webkit-scrollbar { width: 12px; }
            .pool-thick-scroll::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 6px; }
            .pool-thick-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; border: 2px solid #e2e8f0; }
            .pool-thick-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
            .pool-thick-scroll { scrollbar-width: auto; scrollbar-color: #94a3b8 #e2e8f0; }
          `}</style>

          {/* ------ Carousel header ------ */}
          {sortedProjects.length > 0 && (
            <div className="flex items-center justify-between mb-2 px-1">
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 px-2"
                disabled={activeIdx === 0}
                onClick={goPrev}
                data-testid="mob-sched-prev"
              ><ChevronLeft className="w-4 h-4" /></Button>
              <div className="text-xs text-slate-600">
                Project <span className="font-semibold text-slate-900">{activeIdx + 1}</span> of {sortedProjects.length}
              </div>
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 px-2"
                disabled={activeIdx >= sortedProjects.length - 1}
                onClick={goNext}
                data-testid="mob-sched-next"
              ><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}

          {/* ------ Carousel ------ */}
          {sortedProjects.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">No active projects to schedule.</div>
          ) : (
            <div
              ref={carouselRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1"
              data-testid="mob-sched-carousel"
              style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
            >
              {sortedProjects.map((p, idx) => {
                const proj = byProject[p.id] || { team_members: [], start_time: '07:00', end_time: '15:00', saved: false };
                const lastTeam = (lastDayTeamByProject[p.id]?.members || [])
                  .map(uid => (employees || []).find(e => empKey(e) === uid))
                  .filter(emp => emp && !proj.team_members.includes(empKey(emp)));
                return (
                  <div
                    key={p.id}
                    data-card-idx={idx}
                    data-testid={`mob-sched-project-card-${p.id}`}
                    className="snap-start shrink-0 w-[calc(100%-1.5rem)] sm:w-[460px] rounded-xl border bg-white shadow-sm p-3"
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-slate-900 truncate">{p.name}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                          {projectRecency[p.id] ? `Last scheduled ${projectRecency[p.id]}` : 'Never scheduled'}
                          {proj.saved && <Badge className="bg-emerald-100 text-emerald-700 border-0 h-5 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Saved</Badge>}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10px]" data-testid={`mob-sched-card-count-${p.id}`}>
                        {proj.team_members.length} assigned
                      </Badge>
                    </div>

                    {/* Times */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <Label className="text-[10px] text-slate-500">Start</Label>
                        <div className="relative">
                          <Clock className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                          <Input
                            type="time"
                            value={proj.start_time}
                            onChange={(e) => updateProj(p.id, { start_time: e.target.value })}
                            className="pl-7 h-8 text-xs"
                            data-testid={`mob-sched-start-${p.id}`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">End</Label>
                        <Input
                          type="time"
                          value={proj.end_time}
                          onChange={(e) => updateProj(p.id, { end_time: e.target.value })}
                          className="h-8 text-xs"
                          data-testid={`mob-sched-end-${p.id}`}
                        />
                      </div>
                    </div>

                    {/* Last day team (nearest above drop zone) */}
                    {lastTeam.length > 0 && (
                      <div className="mb-2 rounded-lg border bg-amber-50/60 px-2 py-1.5">
                        <div className="text-[10px] font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                          <RotateCcw className="w-3 h-3" />
                          Last day's team ({lastDayTeamByProject[p.id]?.date})
                          <button
                            type="button"
                            onClick={() => {
                              const ids = lastTeam.map(empKey).filter(uid => !bookedElsewhereMap[uid] && !sessionAssignedSet.has(uid));
                              if (ids.length === 0) { toast.info('All of last day\'s team are busy elsewhere'); return; }
                              setByProject(prev => {
                                const cur = prev[p.id] || { team_members: [], start_time: '07:00', end_time: '15:00', saved: false };
                                const merged = Array.from(new Set([...cur.team_members, ...ids]));
                                return { ...prev, [p.id]: { ...cur, team_members: merged, saved: false } };
                              });
                              toast.success(`Added ${ids.length} from last day's team`);
                            }}
                            className="ml-auto text-[10px] text-blue-600 hover:underline font-semibold"
                            data-testid={`mob-sched-add-all-last-${p.id}`}
                          >+ Add all</button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {lastTeam.map(emp => (
                            <Chip
                              key={empKey(emp)}
                              id={`pool:${empKey(emp)}`}
                              employee={emp}
                              compact
                              disabled={!!bookedElsewhereMap[empKey(emp)] || sessionAssignedSet.has(empKey(emp))}
                              hint={bookedElsewhereMap[empKey(emp)] ? `@ ${bookedElsewhereMap[empKey(emp)]}` : (sessionAssignedSet.has(empKey(emp)) ? 'in another project' : null)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Drop zone */}
                    <DropArea
                      id={`proj:${p.id}`}
                      isEmpty={proj.team_members.length === 0}
                      label="Drag employees here"
                      testId={`mob-sched-dropzone-${p.id}`}
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {proj.team_members.map(uid => {
                          const emp = (employees || []).find(e => empKey(e) === uid);
                          if (!emp) return null;
                          return (
                            <Chip
                              key={uid}
                              id={`proj:${p.id}:${uid}`}
                              employee={emp}
                              onRemove={() => removeFromProject(p.id, uid)}
                            />
                          );
                        })}
                      </div>
                    </DropArea>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled={proj.team_members.length === 0}
                        onClick={() => updateProj(p.id, { team_members: [], saved: false })}
                        data-testid={`mob-sched-clear-${p.id}`}
                      >Clear</Button>
                      <Button
                        type="button"
                        size="sm"
                        className="flex-1"
                        disabled={proj.team_members.length === 0 || savingProj === p.id}
                        onClick={() => saveProject(p.id)}
                        data-testid={`mob-sched-save-${p.id}`}
                      >{savingProj === p.id ? 'Saving…' : (proj.saved ? 'Save again' : 'Save')}</Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        disabled={proj.team_members.length === 0 || savingProj === p.id || idx >= sortedProjects.length - 1}
                        onClick={() => saveAndNext(p.id)}
                        data-testid={`mob-sched-save-next-${p.id}`}
                      >Save & Next →</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Drag preview overlay */}
          <DragOverlay dropAnimation={null}>
            {activeDragEmployee ? (
              <Chip id="overlay" employee={activeDragEmployee} />
            ) : null}
          </DragOverlay>
        </DndContext>

        <div className="flex justify-end pt-2 border-t mt-2">
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="mob-sched-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Pool droppable wrapper that still renders children (Chips) inside.
function PoolDrop({ id, children }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      data-testid="mob-sched-pool-drop"
      className={isOver ? 'rounded-xl ring-2 ring-blue-400 ring-offset-1' : ''}
    >
      {children}
    </div>
  );
}

export default MobileScheduleBuilder;
